import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Plus, Trash2, Clock, AlertCircle, Target } from 'lucide-react';

const timeframes = ['3m', '6m', '9m'];

const createObjectiveId = () => `obj_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Create a fresh objective with unique actions array
const createNewObjective = (timeframe = '3m') => ({
  id: createObjectiveId(),
  timeframe,
  description: '',
  dailyTime: '00:30',
  actions: ['', '', '']
});

const timeframeLabels = {
  '3m': '3 Meses',
  '6m': '6 Meses',
  '9m': '9 Meses'
};

const timeframeDescriptions = {
  '3m': 'Lo que querés ordenar primero.',
  '6m': 'Lo que necesitás sostener en el mediano plazo.',
  '9m': 'La dirección grande que querés construir.'
};

const timeframeColors = {
  '3m': 'bg-green-100 border-green-300 dark:bg-green-900/30 dark:border-green-700',
  '6m': 'bg-blue-100 border-blue-300 dark:bg-blue-900/30 dark:border-blue-700',
  '9m': 'bg-purple-100 border-purple-300 dark:bg-purple-900/30 dark:border-purple-700'
};

const normalizeActions = (actions) => {
  const normalized = Array.isArray(actions) ? [...actions] : [];
  while (normalized.length < 3) normalized.push('');
  return normalized;
};

const normalizeObjective = (obj = {}, timeframe = '3m') => ({
  id: obj.id || createObjectiveId(),
  timeframe,
  description: obj.description || '',
  dailyTime: obj.dailyTime || '00:30',
  actions: normalizeActions(obj.actions)
});

const ensureOneObjectivePerTimeframe = (list) => {
  const normalized = list.map((obj) => normalizeObjective(obj, obj.timeframe || '3m'));

  timeframes.forEach((timeframe) => {
    if (!normalized.some((obj) => obj.timeframe === timeframe)) {
      normalized.push(createNewObjective(timeframe));
    }
  });

  return normalized;
};

const normalizeObjectivesToList = (objectives) => {
  if (objectives && (objectives['3m'] || objectives['6m'] || objectives['9m'])) {
    const converted = [];

    timeframes.forEach((period) => {
      const periodObjs = objectives[period] || [];
      if (periodObjs.length > 0) {
        periodObjs.forEach((obj) => {
          converted.push(normalizeObjective(obj, period));
        });
      } else {
        converted.push(createNewObjective(period));
      }
    });

    return ensureOneObjectivePerTimeframe(converted);
  }

  if (Array.isArray(objectives) && objectives.length > 0) {
    return ensureOneObjectivePerTimeframe(objectives);
  }

  return timeframes.map((timeframe) => createNewObjective(timeframe));
};

export function ObjectivesFormNew({ objectives, onChange, showErrors = false }) {
  const [objectivesList, setObjectivesList] = useState(() => normalizeObjectivesToList(objectives));

  // Sync changes to parent in the legacy backend format: { 3m: [], 6m: [], 9m: [] }.
  useEffect(() => {
    const legacyFormat = {
      '3m': [],
      '6m': [],
      '9m': []
    };

    objectivesList.forEach(obj => {
      if (legacyFormat[obj.timeframe]) {
        legacyFormat[obj.timeframe].push({
          description: obj.description,
          dailyTime: obj.dailyTime,
          actions: [...obj.actions]
        });
      }
    });

    onChange(legacyFormat);
  }, [objectivesList, onChange]);

  const handleObjectiveChange = (index, field, value) => {
    setObjectivesList(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleActionChange = (objIndex, actionIndex, value) => {
    setObjectivesList(prev => {
      const updated = [...prev];
      const newActions = [...updated[objIndex].actions];
      newActions[actionIndex] = value;
      updated[objIndex] = { ...updated[objIndex], actions: newActions };
      return updated;
    });
  };

  const addObjective = (timeframe) => {
    setObjectivesList(prev => [...prev, createNewObjective(timeframe)]);
  };

  const removeObjective = (index) => {
    const timeframe = objectivesList[index]?.timeframe;
    const periodCount = objectivesList.filter((obj) => obj.timeframe === timeframe).length;

    if (periodCount > 1) {
      setObjectivesList(prev => prev.filter((_, i) => i !== index));
    }
  };

  const addAction = (objIndex) => {
    setObjectivesList(prev => {
      const updated = [...prev];
      updated[objIndex] = {
        ...updated[objIndex],
        actions: [...updated[objIndex].actions, '']
      };
      return updated;
    });
  };

  // Validation: at least one objective with description
  const hasValidObjective = objectivesList.some(obj => obj.description?.trim());

  return (
    <div className="space-y-6">
      {showErrors && !hasValidObjective && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-700">
            <strong>Atención:</strong> Necesitás completar al menos 1 objetivo con descripción.
          </div>
        </div>
      )}

      {timeframes.map((timeframe) => {
        const periodObjectives = objectivesList
          .map((obj, index) => ({ ...obj, index }))
          .filter((obj) => obj.timeframe === timeframe);

        return (
          <section key={timeframe} className="space-y-4" data-testid={`objective-section-${timeframe}`}>
            <div className={`rounded-xl border-2 p-4 ${timeframeColors[timeframe]}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold">Objetivos a {timeframeLabels[timeframe].toLowerCase()}</h3>
                  <p className="text-sm text-muted-foreground">{timeframeDescriptions[timeframe]}</p>
                </div>
                <span className="rounded-full bg-white/70 px-3 py-1 text-xs font-medium text-foreground dark:bg-black/20">
                  {periodObjectives.filter((obj) => obj.description?.trim()).length} completos
                </span>
              </div>
            </div>

            {periodObjectives.map((obj, periodIndex) => {
              const hasError = showErrors && !hasValidObjective && !obj.description?.trim();
              const canRemove = periodObjectives.length > 1;

              return (
                <Card
                  key={obj.id || `${timeframe}-${periodIndex}`}
                  className={`relative border-2 ${timeframeColors[timeframe]} ${hasError ? 'ring-2 ring-red-500' : ''}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Target className="w-5 h-5 text-primary" />
                        <CardTitle className="text-base">Objetivo {periodIndex + 1}</CardTitle>
                      </div>
                      {canRemove && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeObjective(obj.index)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          data-testid={`remove-objective-${timeframe}-${periodIndex}`}
                          aria-label={`Eliminar objetivo ${periodIndex + 1} de ${timeframeLabels[timeframe]}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1">
                        Descripción del objetivo
                        <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        value={obj.description || ''}
                        onChange={(e) => handleObjectiveChange(obj.index, 'description', e.target.value)}
                        placeholder="¿Qué quieres lograr?"
                        className={`bg-white dark:bg-background ${hasError ? 'border-red-500 focus:ring-red-500' : ''}`}
                        data-testid={`objective-description-${timeframe}-${periodIndex}`}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Tiempo diario a dedicar (HH:MM)
                      </Label>
                      <Input
                        type="text"
                        value={obj.dailyTime || '00:30'}
                        onChange={(e) => handleObjectiveChange(obj.index, 'dailyTime', e.target.value)}
                        placeholder="00:30"
                        className="w-32 bg-white dark:bg-background"
                        data-testid={`objective-time-${timeframe}-${periodIndex}`}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Acciones a realizar</Label>
                      <div className="space-y-2">
                        {(obj.actions || ['', '', '']).map((action, actionIndex) => (
                          <div key={actionIndex} className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground w-6">
                              {actionIndex + 1}.
                            </span>
                            <Textarea
                              value={action}
                              onChange={(e) => handleActionChange(obj.index, actionIndex, e.target.value)}
                              placeholder={`Acción ${actionIndex + 1}`}
                              rows={1}
                              className="flex-1 bg-white dark:bg-background"
                              data-testid={`objective-action-${timeframe}-${periodIndex}-${actionIndex}`}
                            />
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => addAction(obj.index)}
                          className="text-muted-foreground hover:text-primary"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Agregar acción
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            <Button
              type="button"
              variant="outline"
              onClick={() => addObjective(timeframe)}
              className="w-full py-5 border-dashed border-2 hover:border-primary hover:bg-primary/5"
              data-testid={`add-objective-${timeframe}`}
            >
              <Plus className="w-5 h-5 mr-2" />
              Agregar otro objetivo a {timeframeLabels[timeframe]}
            </Button>
          </section>
        );
      })}

      {/* Summary of objectives by timeframe */}
      {objectivesList.filter(o => o.description?.trim()).length > 0 && (
        <div className="mt-4 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-medium mb-2">Resumen de Objetivos:</h4>
          <div className="grid grid-cols-3 gap-2 text-sm">
            {timeframes.map(period => {
              const count = objectivesList.filter(o => o.timeframe === period && o.description?.trim()).length;
              return (
                <div key={period} className={`p-2 rounded text-center ${count > 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-800'}`}>
                  <div className="font-medium">{timeframeLabels[period]}</div>
                  <div className={count > 0 ? 'text-green-700 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}>
                    {count} objetivo{count !== 1 ? 's' : ''}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default ObjectivesFormNew;
